import apiClient from '../../../../api/axios';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  TCustomersResponse,
  TCustomerResponse,
  TApiStatsResponse,
  TApiGrowthResponse,
  TApiTopRestaurantsResponse,
} from '../schemas';

const CUSTOMERS_BASE_URL = '/restaurant-customer';
const QUERY_KEY = 'restaurant-customers';

// Get all customers (paginated)
export const useGetCustomers = (restaurantId: string, page = 1, limit = 10) => {
  return useQuery({
    queryKey: [QUERY_KEY, restaurantId, page, limit],
    queryFn: async () => {
      const response = await apiClient.get<TCustomersResponse>(CUSTOMERS_BASE_URL, {
        headers: {
          'Restaurant-ID': restaurantId,
        },
        params: {
          pageNum: page,
          pageSize: limit,
        },
      });
      return {
        rows: response.data?.data?.rows || [],
        count: response.data?.data?.total || 0,
        pageNum: response.data?.data?.pageNum || 1,
        pageSize: response.data?.data?.pageSize || limit,
        pages: response.data?.data?.pages || 0,
      };
    },
    enabled: !!restaurantId,
  });
};

// Get single customer
export const useGetCustomer = (id: string) => {
  return useQuery({
    queryKey: [QUERY_KEY, id],
    queryFn: async () => {
      const response = await apiClient.get<TCustomerResponse>(`${CUSTOMERS_BASE_URL}/${id}`);
      return response.data?.data;
    },
    enabled: !!id,
  });
};

// Get customer stats
export const useGetCustomerStats = (restaurantId?: string) => {
  return useQuery({
    queryKey: [QUERY_KEY, 'stats', restaurantId],
    queryFn: async () => {
      const config = restaurantId
        ? {
            headers: {
              'Restaurant-ID': restaurantId,
            },
          }
        : undefined;

      const response = await apiClient.get<TApiStatsResponse>(`${CUSTOMERS_BASE_URL}/stats`, config);
      return response.data?.data;
    },
    enabled: !!restaurantId,
  });
};

// Get customer growth
export const useGetCustomerGrowth = (
  fromDate: string,
  toDate: string,
  restaurantId?: string,
) => {
  return useQuery({
    queryKey: [QUERY_KEY, 'growth', restaurantId, fromDate, toDate],
    queryFn: async () => {
      const config: any = {
        params: {
          fromDate,
          toDate,
        },
      };

      if (restaurantId) {
        config.headers = {
          'Restaurant-ID': restaurantId,
        };
      }

      const response = await apiClient.get<TApiGrowthResponse>(`${CUSTOMERS_BASE_URL}/growth`, config);
      return response.data?.data?.growth || [];
    },
    enabled: !!fromDate && !!toDate && !!restaurantId,
  });
};

// Get top restaurants by customers
export const useGetTopRestaurants = (topLimit = 5) => {
  return useQuery({
    queryKey: [QUERY_KEY, 'top-restaurants', topLimit],
    queryFn: async () => {
      const response = await apiClient.get<TApiTopRestaurantsResponse>(
        `${CUSTOMERS_BASE_URL}/top-restaurants`,
        {
          params: {
            topLimit,
          },
        }
      );
      return response.data?.data?.topRestaurants || [];
    },
  });
};

// Fetch all customers for export
export const useExportCustomers = () => {
  return useMutation({
    mutationFn: async (restaurantId: string) => {
      const response = await apiClient.get(CUSTOMERS_BASE_URL, {
        headers: {
          'Restaurant-ID': restaurantId,
        },
        params: {
          pageNum: 1,
          pageSize: 10000, // Maximum limit for export
        },
      });
      return response.data?.data?.rows || [];
    },
  });
};

export const customersService = {
  useGetCustomers,
  useGetCustomer,
  useGetCustomerStats,
  useGetCustomerGrowth,
  useGetTopRestaurants,
  useExportCustomers,
};
